import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { getSiteContentByKey } from "@/services/site";
import logger from "@/runtime-utils/observability/logger";

export default function AnnouncementBanner() {
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [banner, setBanner] = useState<{
    message: string;
    isActive: boolean;
    priority?: string;
    isPermanent?: boolean;
  } | null>(null);
  const [visible, setVisible] = useState(false);

  const priorityColors: Record<string, string> = {
    Critical: "#D32F2F",
    Warning: "#FFAE1F",
    Information: "#539BFF",
    Success: "#4CAF50",
  };

  useEffect(() => {
    const fetchSettings = async () => {
      const response = await getSiteContentByKey("global_announcement_banner");
      if (response?.success && response?.data?.content) {
        try {
          const parsedContent = JSON.parse(response.data.content);
          const { message, isActive, priority, isPermanent } = parsedContent;
          const isPerm = isPermanent ?? true; // default to true based on req

          if (isActive && message) {
            if (!isPerm) {
              const dismissedMessage = localStorage.getItem(
                "dismissed_banner_msg"
              );
              if (dismissedMessage !== message) {
                setBanner({ message, isActive, priority, isPermanent: isPerm });
                setVisible(true);
              } else {
                setVisible(false);
              }
            } else {
              setBanner({ message, isActive, priority, isPermanent: isPerm });
              setVisible(true);
            }
          } else {
            setVisible(false);
            setBanner(null);
          }
        } catch (e) {
          logger.error("Failed to parse banner settings", e);
        }
      }
    };

    const loadBanner = () => {
      if (window.location.pathname !== "/sign-in") {
        void fetchSettings();
      }
    };

    loadBanner();
    const intervalId = setInterval(loadBanner, 30000); // Poll every 30 seconds

    return () => clearInterval(intervalId);
  }, []);

  const handleClose = () => {
    if (banner?.message) {
      localStorage.setItem("dismissed_banner_msg", banner.message);
    }
    setVisible(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.documentElement;

    const updateBannerHeight = () => {
      const height =
        visible && bannerRef.current
          ? Math.ceil(bannerRef.current.getBoundingClientRect().height)
          : 0;
      root.style.setProperty("--announcement-banner-height", `${height}px`);
    };

    updateBannerHeight();

    let resizeObserver: ResizeObserver | null = null;
    if (visible && bannerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateBannerHeight);
      resizeObserver.observe(bannerRef.current);
    }

    window.addEventListener("resize", updateBannerHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateBannerHeight);
      root.style.setProperty("--announcement-banner-height", "0px");
    };
  }, [visible, banner?.message, banner?.priority, banner?.isPermanent]);

  if (!visible || !banner) {
    return null;
  }

  return (
    <Box
      ref={bannerRef}
      sx={{
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: banner.priority
          ? priorityColors[banner.priority] || "#4CAF50"
          : "#4CAF50",
        minHeight: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        padding: "8px 48px",
        zIndex: 1000,
      }}
    >
      <Typography
        sx={{
          color: "#FFFFFF",
          fontSize: "14px",
          textAlign: "center",
          fontWeight: 500,
        }}
      >
        {banner.message}
      </Typography>
      {!banner.isPermanent && (
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: "16px",
            color: "#FFFFFF",
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
